import { forwardRef, useMemo, useState } from 'react';

import { RiCalendarLine, RiErrorWarningLine } from 'react-icons/ri';

import {
  MonthPickerInput as MantineMonthPickerInput,
  MonthPickerInputProps
} from '@mantine/dates';

import { useMergeClassNames } from '../../utils';

import { MonthPickerInputOverrideClassNames } from './MonthPickerInput.styles';

/**
 * Editable date input
 * Use DatePicker input to prevent user from editing, it also support range, multi select
 */
const MonthPickerInput = forwardRef<
  HTMLButtonElement,
  MonthPickerInputProps<'default'>
>(
  (
    {
      size = 'md',
      error,
      leftSection,
      rightSection,
      valueFormat = 'MMMM',
      classNames,
      onFocus,
      onBlur,
      ...rest
    },
    ref
  ) => {
    const rightSectionFinal = error ? (
      <RiErrorWarningLine />
    ) : rightSection ? (
      rightSection
    ) : undefined;
    const [focussed, setFocussed] = useState<boolean>(false);

    const classes = useMemo(() => {
      return MonthPickerInputOverrideClassNames({
        size,
        focussed,
        error,
        rightSection: rightSectionFinal
      });
    }, [size, focussed, error, rightSectionFinal]);

    const handleFocus = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      setFocussed(true);
      onFocus && onFocus(e);
    };

    const handleBlur = (e: React.FocusEvent<HTMLButtonElement, Element>) => {
      setFocussed(false);
      onBlur && onBlur(e);
    };

    const mergedClassNames = useMergeClassNames({
      classNames: [classes, classNames]
    });

    return (
      <MantineMonthPickerInput<'default'>
        {...rest}
        ref={ref}
        leftSection={leftSection ? leftSection : <RiCalendarLine />}
        size={size}
        error={error}
        rightSection={rightSectionFinal}
        valueFormat={valueFormat}
        onFocus={handleFocus}
        onBlur={handleBlur}
        classNames={mergedClassNames}
      />
    );
  }
);

export { MonthPickerInput };
export type { MonthPickerInputProps };
